import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm } from 'antd';
import { supplierApi, type SupplierDTO } from '../api/kfc';

const SupplierManage: React.FC = () => {
  const [list, setList] = useState<SupplierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    supplierApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载供应商列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (r: SupplierDTO) => {
    setEditingId(r.id ?? null);
    form.setFieldsValue({
      supplierId: r.supplierId,
      supplierName: r.supplierName,
      contact: r.contact,
      phoneNumber: r.phoneNumber,
      address: r.address,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, any>) => {
    const dto: Partial<SupplierDTO> = {
      supplierId: values.supplierId,
      supplierName: values.supplierName,
      contact: values.contact,
      phoneNumber: values.phoneNumber,
      address: values.address,
    };
    const submit = editingId ? supplierApi.update(editingId, dto) : supplierApi.create(dto);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onDelete = (id: string) => {
    supplierApi
      .delete(id)
      .then(() => {
        message.success('删除成功');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card
        title="供应商列表"
        extra={<Button type="primary" onClick={openAdd}>新增供应商</Button>}
      >
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 800 }}
          columns={[
            { title: '供应商标识', dataIndex: 'supplierId', width: 140, ellipsis: true },
            { title: '供应商名称', dataIndex: 'supplierName', width: 140, ellipsis: true },
            { title: '联系人', dataIndex: 'contact', width: 100, ellipsis: true },
            { title: '电话', dataIndex: 'phoneNumber', width: 130, ellipsis: true },
            { title: '地址', dataIndex: 'address', width: 200, ellipsis: true },
            {
              title: '操作',
              width: 160,
              fixed: 'right',
              render: (_, r) => (
                <>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定要删除该供应商吗？"
                    onConfirm={() => r.id && onDelete(r.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑供应商' : '新增供应商'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="supplierId"
            label="供应商标识"
            rules={[{ required: true, message: '请输入供应商标识' }]}
          >
            <Input placeholder="供应商标识" disabled={!!editingId} />
          </Form.Item>
          <Form.Item
            name="supplierName"
            label="供应商名称"
            rules={[{ required: true, message: '请输入供应商名称' }]}
          >
            <Input placeholder="供应商名称" />
          </Form.Item>
          <Form.Item name="contact" label="联系人">
            <Input placeholder="联系人" />
          </Form.Item>
          <Form.Item name="phoneNumber" label="电话">
            <Input placeholder="电话" />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input.TextArea placeholder="地址" rows={2} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SupplierManage;
